/**
 * 광고 코드값의 **사람이 읽는 이름** 한 벌 — 두 화면이 같은 표를 읽는다.
 *
 * 경계(`types/ads-meta`)가 나르는 것은 코드값(`VIDEO` · `brand-site` · `KR`)뿐이고, 무슨
 * 말로 보여 줄지는 화면 몫이다. 그 이름이 두 곳에 따로 적혀 있으면 소리 없이 갈라지므로
 * 표는 여기 하나만 둔다.
 *
 * ## ⚠️ 두 화면이 **일부러 다르게 부른다** — 표기는 두 벌이다
 *
 * ```
 *   full   Meta 광고 레퍼런스 — 목록·상세를 다 보여 주는 전문 화면
 *   short  통합 트렌드 검색   — 카드 한 줄에 얹는 요약
 * ```
 *
 * `DCO(동적)` ↔ `DCO` 처럼 길이가 다른 자리가 있어 `variant` 로 가른다. 한쪽으로 통일하면
 * 그 화면의 표시가 바뀐다 — 통일 여부는 제품 판단이라 아직 갈라 둔다.
 *
 * ## ⚠️ **아는 코드의 범위도 화면마다 다르다**
 *
 * 표에 있다고 화면이 다 아는 것이 아니다. 각 화면이 `pickAdLabels` 에 자기 코드 목록을
 * 넘겨 **자기가 아는 것만** 가져간다. 모르는 코드는 화면의 폴백(코드 원문 또는 빈 문자열)
 * 으로 떨어지는데, 그 폴백이 지금 화면에 그려지는 값이다 — 목록을 넓히면 표시가 바뀐다.
 */

/** 화면별 표기 — `full` 은 전문 화면, `short` 는 카드 요약 */
export type AdLabelVariant = 'full' | 'short';

/** 표기가 하나면 문자열, 화면마다 다르면 두 벌 */
export type AdLabelEntry = string | { full: string; short: string };

/** 표에서 한 화면 몫의 이름표를 뜬다 — `codes` 를 주면 그 코드만, 안 주면 표 전체 */
export function pickAdLabels(
  table: Record<string, AdLabelEntry>,
  variant: AdLabelVariant,
  codes?: readonly string[],
): Record<string, string> {
  const keys = codes ?? Object.keys(table);
  const out: Record<string, string> = {};
  keys.forEach((code) => {
    const entry = table[code];
    if (entry === undefined) return;
    out[code] = typeof entry === 'string' ? entry : entry[variant];
  });
  return out;
}

/** 광고 소재 포맷 */
export const AD_FORMAT_LABELS: Record<string, AdLabelEntry> = {
  VIDEO: '영상',
  IMAGE: '이미지',
  MULTI_IMAGES: '멀티이미지',
  MULTI_VIDEOS: '멀티영상',
  MULTI_MEDIAS: '멀티미디어',
  CAROUSEL: '캐러셀',
  DCO: { full: 'DCO(동적)', short: 'DCO' },
  DPA: { full: 'DPA(다이내믹 상품)', short: 'DPA' },
  TEXT: '텍스트',
  PAGE_LIKE: '페이지 좋아요',
  EVENT: '이벤트',
};

/** 광고 노출 국가 */
export const AD_COUNTRY_LABELS: Record<string, AdLabelEntry> = {
  KR: '한국',
  US: '미국',
  JP: '일본',
  CN: '중국',
  TW: '대만',
  HK: '홍콩',
  SG: '싱가포르',
  TH: '태국',
  VN: '베트남',
  ID: '인도네시아',
  MY: '말레이시아',
  PH: '필리핀',
  IN: '인도',
  BD: '방글라데시',
  GB: '영국',
  FR: '프랑스',
  DE: '독일',
  ES: '스페인',
  BR: '브라질',
  AU: '호주',
  CA: '캐나다',
  unknown: '미상',
};

/** 광고가 보내는 곳 */
export const AD_LANDING_PLATFORM_LABELS: Record<string, AdLabelEntry> = {
  'brand-site': { full: '자사몰/기타', short: '자사몰' },
  amazon: 'Amazon',
  oliveyoung: '올리브영',
  shopee: 'Shopee',
  naver: '네이버',
  coupang: '쿠팡',
  lazada: 'Lazada',
  yesstyle: 'YesStyle',
  qoo10: 'Qoo10',
  blinkit: 'Blinkit',
  instagram: 'Instagram',
  facebook: 'Facebook',
  whatsapp: 'WhatsApp',
  'google-play': 'Google Play',
  'app-store': 'App Store',
  'link-in-bio': 'Link in bio',
};

/** 광고가 내건 혜택(`offerType`) */
export const AD_OFFER_LABELS: Record<string, AdLabelEntry> = {
  discount: '할인',
  bundle: '세트 구성',
  gift: '증정',
  scarcity: '한정·마감 임박',
  bogo: '1+1',
  firstpurchase: '첫 구매 혜택',
  refund: '환불 보장',
};

/** 광고 문구의 효능 주장 유형(`claimFlags`) */
export const AD_CLAIM_LABELS: Record<string, AdLabelEntry> = {
  'functional-cert': '기능성 인증',
  clinical: '임상 결과',
  'numeric-efficacy': '수치 효능',
  'decimal-precision': '소수점 수치',
  'depth-claim': '흡수 깊이',
  'drug-comparison': '의약품 비교',
  'before-after': '사용 전후 비교',
};

/** 광고를 누가 돌리는가(`deliverySegment`) */
export const AD_DELIVERY_SEGMENT_LABELS: Record<string, AdLabelEntry> = {
  inhouse: '브랜드 직접',
  reseller: '판매처',
  partnership: '파트너십',
  creator_self: '크리에이터 직접',
  whitelisted: '크리에이터 계정 대행',
};

/**
 * Meta CTA 버튼(`ctaType`) → 한국어 문구. Meta 광고 관리자의 한국어 버튼 이름을 쓴다.
 *
 * ⚠️ 원장의 `ctaText` 는 광고주가 고른 언어 그대로다(영어가 많다) — 화면은 이 표를 먼저 보고, 모르는 코드만 원문을 쓴다.
 */
export const AD_CTA_LABELS: Record<string, AdLabelEntry> = {
  SHOP_NOW: '지금 구매하기',
  LEARN_MORE: '더 알아보기',
  SEE_DETAILS: '자세히 보기',
  MESSAGE_PAGE: '메시지 보내기',
  INSTAGRAM_MESSAGE: 'Instagram 메시지',
  WHATSAPP_MESSAGE: 'WhatsApp 메시지',
  VIEW_INSTAGRAM_PROFILE: 'Instagram 프로필 보기',
  VISIT_PROFILE: '프로필 방문하기',
  ORDER_NOW: '지금 주문하기',
  BUY_NOW: '지금 구매하기',
  SIGN_UP: '가입하기',
  SUBSCRIBE: '구독하기',
  BOOK_TRAVEL: '지금 예약하기',
  BOOK_NOW: '지금 예약하기',
  CONTACT_US: '문의하기',
  GET_IN_TOUCH: '문의하기',
  CALL_NOW: '지금 전화하기',
  APPLY_NOW: '지금 신청하기',
  INSTALL_MOBILE_APP: '앱 설치하기',
  USE_MOBILE_APP: '앱 사용하기',
  USE_APP: '앱 사용하기',
  DOWNLOAD: '다운로드',
  WATCH_MORE: '더 보기',
  PLAY_GAME: '게임하기',
  GET_OFFER: '혜택 받기',
  GET_OFFER_VIEW: '혜택 보기',
  GET_PROMOTIONS: '프로모션 보기',
  GET_QUOTE: '견적 받기',
  GET_DIRECTIONS: '길 찾기',
  GET_SHOWTIMES: '상영 시간 보기',
  EVENT_RSVP: '참석 신청하기',
  REQUEST_TIME: '시간 요청하기',
  CHECK_AVAILABILITY: '예약 가능 여부 확인',
  BUY_TICKETS: '티켓 구매하기',
  VIEW_PRODUCT: '제품 보기',
  SEE_MENU: '메뉴 보기',
  PAY_TO_ACCESS: '결제하고 보기',
  OPEN_LINK: '링크 열기',
  LIKE_PAGE: '페이지 좋아요',
  NO_BUTTON: '',
};

/**
 * **미리보기 버튼에 새기는 CTA 문구** — 위 `AD_CTA_LABELS` 와 **일부러 다른 표다.**
 *
 * 위 표는 필터 칩과 통계 축에 쓰는 이름이고(「Instagram 메시지」), 이 표는 소재 위에 실제 광고처럼
 * 얹는 버튼 문구다(「메시지 보내기」). 광고가 화면에 그려 놓은 버튼과 글자가 달라지면 안 되므로
 * 한쪽으로 합치지 않는다 — 합치면 **화면의 글자가 바뀐다.**
 *
 * ⚠️ `NO_BUTTON` 은 **빈 문자열**이다 — 버튼을 그리지 않는다는 뜻이라 지우면 안 된다.
 */
export const AD_CTA_BUTTON_LABELS: Record<string, string> = {
  SHOP_NOW: '지금 구매하기',
  LEARN_MORE: '더 알아보기',
  SEE_DETAILS: '자세히 보기',
  ORDER_NOW: '지금 주문하기',
  MESSAGE_PAGE: '메시지 보내기',
  INSTAGRAM_MESSAGE: '메시지 보내기',
  WHATSAPP_MESSAGE: 'WhatsApp 문의',
  VIEW_INSTAGRAM_PROFILE: '프로필 보기',
  VISIT_PROFILE: '프로필 보기',
  SIGN_UP: '가입하기',
  APPLY_NOW: '지금 신청하기',
  BOOK_TRAVEL: '지금 예약하기',
  BOOK_NOW: '지금 예약하기',
  INSTALL_MOBILE_APP: '앱 설치하기',
  DOWNLOAD: '다운로드',
  CONTACT_US: '문의하기',
  CALL_NOW: '지금 전화하기',
  PAY_TO_ACCESS: '결제하고 보기',
  WATCH_MORE: '더 보기',
  GET_OFFER: '혜택 받기',
  GET_OFFER_VIEW: '혜택 보기',
  GET_PROMOTIONS: '프로모션 보기',
  SUBSCRIBE: '구독하기',
  LIKE_PAGE: '페이지 좋아요',
  NO_BUTTON: '',
};

/**
 * 미리보기 버튼에 쓸 문구 — 없으면 빈 문자열이고, 그러면 **버튼을 그리지 않는다.**
 *
 * ⚠️ `ctaType` 을 먼저 본다. `ctaText` 는 수집 계정 언어 그대로라 영문(「Shop now」)이 섞인다.
 */
export function ctaButtonLabelOf(creative: { ctaType?: string; ctaText?: string }): string {
  const type = String(creative?.ctaType || '').toUpperCase();
  if (type && AD_CTA_BUTTON_LABELS[type] != null) return AD_CTA_BUTTON_LABELS[type];
  return String(creative?.ctaText || '').trim();
}
